import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CooccurrenceWarning } from '../../../../line-item-entry/models/cooccurrence-warning.interface';

export interface MissingItemsDialogData {
  warnings: CooccurrenceWarning[];
}

// A missing item (B) and every item already on the invoice (A) that predicts it.
export interface MissingItemGroup {
  bCode: string;
  bName: string;
  matches: CooccurrenceWarning[];   // present items, sorted by confidencePct desc
  maxConfidence: number;            // strongest single match — shown in the collapsed header
  cumulativeConfidence: number;     // sum of match confidences — drives group ordering
}

@Component({
  selector: 'app-missing-items-dialog',
  templateUrl: './missing-items.dialog.html',
  styleUrls: ['./missing-items.dialog.scss']
})
export class MissingItemsDialogComponent {
  // #2700: a missing item whose strongest predictor is at least this confident gets a
  // distinct "high confidence" treatment so staff can triage by scanning, not reading numbers.
  // Presentation-only: this changes styling, never which warnings are shown or whether submit
  // proceeds (the server alone decides that). It is a client-side constant, not read from the
  // server's MissingLineItemsOptions.BaseConfidencePct (90 by default), so retuning the server's
  // confidence settings does not move it; revisit this value if those are retuned.
  static readonly highConfidenceThreshold = 85;

  groups: MissingItemGroup[];

  constructor(
    public dialogRef: MatDialogRef<MissingItemsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MissingItemsDialogData
  ) {
    this.groups = this.buildGroups(data.warnings || []);
  }

  isHighConfidence(group: MissingItemGroup): boolean {
    return group.maxConfidence >= MissingItemsDialogComponent.highConfidenceThreshold;
  }

  // Collapse the flat (present item -> missing item) pairs into one group per
  // missing item, so the popup lists each missing item once with its predictors.
  private buildGroups(warnings: CooccurrenceWarning[]): MissingItemGroup[] {
    const byMissingItem = new Map<string, MissingItemGroup>();

    for (const warning of warnings) {
      const key = warning.bCode || warning.bName;
      let group = byMissingItem.get(key);
      if (!group) {
        group = {
          bCode: warning.bCode,
          bName: warning.bName,
          matches: [],
          maxConfidence: 0,
          cumulativeConfidence: 0
        };
        byMissingItem.set(key, group);
      }

      group.matches.push(warning);
      group.maxConfidence = Math.max(group.maxConfidence, warning.confidencePct);
      group.cumulativeConfidence += warning.confidencePct;
    }

    const groups = Array.from(byMissingItem.values());
    for (const group of groups) {
      group.matches.sort((a, b) => b.confidencePct - a.confidencePct);
    }
    groups.sort((a, b) => b.cumulativeConfidence - a.cumulativeConfidence);
    return groups;
  }

  onReview(): void {
    this.dialogRef.close(false);
  }

  onProceed(): void {
    this.dialogRef.close(true);
  }
}
